import React, { useEffect, useState } from "react";
import Loading from "components/Loading/Loading";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import operatorApi from "api/operator/operator.api";
import { toast } from "react-toastify";

import OperatorSidebar from "./components/OperatorSidebar";
import TodayOpeningSlot from "./components/TodayOpeningSlot";
import { motion, AnimatePresence } from "framer-motion";
import { setAuthModalOpen } from "../../redux/features/authModalSlice";

import Button from "shared/Button/Button";
import MultiSelectModal from "./components/MultiSelectModal";
import sericesApi from "api/service/service.api";
import SelectProfessional from "./components/SelectProfessional";
import staffApi from "api/staff/staff.api";
import TimeSlotSelector from "./components/Timeslotselector";
import ContactInfo from "./components/ContactInfo";
import SummaryCard from "./components/SummaryCard";
import PaymentMethodSelector from "./components/PaymentMethodSelector";
import { Helmet } from "react-helmet-async";
import bookingApi from "api/booking/BookingApi";
import { resetBookingState } from "../../redux/features/cartStateBooking";
import ProcessingBar from "utils/Processingbar";
import { socketId } from "configs/axiosConfig";

const BookingProcess = ({ slug }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user || {});
  const bookingStaff = useSelector((state) => state.cartBooking.bookingStaff);
  const bookingCart = useSelector((state) => state.cartBooking.bookingCart);
  const bookingTime = useSelector((state) => state.cartBooking.bookingTime);
  const bookingPaymentMethod = useSelector(
    (state) => state.cartBooking.bookingPaymentMethod
  );

  useEffect(() => {
    if (bookingCart.length === 0) {
      // Reset all local states
      setSelectedServices([]);
      setSelectedServiceId([]);
      setTotalTimeService(undefined);
      setSelectedDateTime("");
      setSelectedProfessionalId([]);
      setSelectedPayment(); // or "" if you prefer
      setCurrentStep(0);
      setIsProcessing(false);
    }
  }, [bookingCart]);

  const [operator, setOperator] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDataLoading, setIsDataLoading] = useState(true); // Second stage loading

  const [notFound, setNotFound] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);

  //Services
  const [services, setServices] = useState([]);
  const [serviceCategory, setServiceCategory] = useState([]);
  const [selectedServices, setSelectedServices] = useState(bookingCart);
  const [selectedServiceId, setSelectedServiceId] = useState(
    bookingCart.map((item) => item._id) || []
  );
  const [totalTimeService, setTotalTimeService] = useState();

  //booking Time
  const [selectedDateTime, setSelectedDateTime] = useState(
    typeof bookingTime === "string" && bookingTime.trim() !== ""
      ? bookingTime
      : ""
  );
  //Staff
  const [staffs, setStaffs] = useState([]);
  const [selectedProfessionalId, setSelectedProfessionalId] = useState(
    bookingStaff?.[0] || []
  );

  //payment method
  const [selectedPayment, setSelectedPayment] = useState(bookingPaymentMethod); // Default to "cash"

  //Info
  const [name, setName] = useState(user?.displayName);
  const [email, setEmail] = useState(user?.username);
  const [message, setMessage] = useState("");
  const [mobile, setMobile] = useState(user?.phone);

  const getOperator = async () => {
    try {
      const data = await operatorApi.getOperatorBySlug(slug);

      if (!data || !data._id) {
        setNotFound(true);
        return;
      }
      // If your API returns a single object
      setOperator([data]);
    } catch (error) {
      toast.error(`Error fetching operator: ${error.message}`);
      setNotFound(true);
    } finally {
      setIsLoading(false);
    }
  };

  const getServicelData = async () => {
    try {
      const operatorId = operator[0]?._id;

      const { response, err } = await sericesApi.getallService({ operatorId });

      if (response && response.data) {
        setServices(response.data.services);
      } else {
        toast.error("No data found");
      }
    } catch (error) {
      toast.error("Something went wrong!");
      console.error("Error fetching data:", error);
    }
  };

  const getServiceCategories = async () => {
    try {
      const operatorId = operator[0]?._id;

      const response = await sericesApi.getServiceCategory({
        operator: operatorId,
      });
      if (response.success && response.category) {
        setServiceCategory(response.category);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const getAllStaff = async () => {
    try {
      const operatorId = operator[0]?._id;

      const { response, err } = await staffApi.getAllStaffInfo(operatorId);

      if (response && response.data) {
        setStaffs(response.data.data);
      } else {
        toast.error("No data found");
      }
    } catch (error) {
      toast.error("Something went wrong!");
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    if (!slug) return;

    setIsLoading(true);

    const loadOperator = async () => {
      try {
        await getOperator(); // sets operator state
      } catch (err) {
        toast.error(err.message || "Failed to load operator");
        setNotFound(true);
      } finally {
        setIsLoading(false);
      }
    };

    loadOperator();
  }, [slug]);

  // 2️⃣ Second effect: load services after operator is ready
  useEffect(() => {
    if (!operator?.[0]?._id) return; // wait for operator

    setIsDataLoading(true);

    const loadServices = async () => {
      try {
        await getServicelData(operator[0]._id); // pass id directly
        await getServiceCategories(operator[0]._id);
        await getAllStaff();
      } catch (err) {
        toast.error(err.message || "Failed to load services");
      } finally {
        setIsDataLoading(false);
      }
    };

    loadServices();
  }, [operator]);

  // console.log({ operator });
  // console.log({ services });

  const isPageLoading = isLoading || isDataLoading;

  const isStepValid = () => true;
  const isValid = true;
  const stepError = "Please select a valid time slot.";
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === 5;

  const handleBack = () => setCurrentStep((prev) => Math.max(prev - 1, 0));
  const handleNext = () => setCurrentStep((prev) => Math.min(prev + 1, 5));

  const handleSubmit = async () => {
    if (
      !selectedDateTime ||
      !selectedServices.length ||
      !selectedProfessionalId?._id
    ) {
      toast.error("Missing required booking info.");
      return;
    }

    setIsProcessing(true);

    try {
      const totalDuration = parseInt(totalTimeService || 0);
      const subTotalPrice = selectedServices.reduce(
        (acc, cur) => acc + Number(cur.price || 0),
        0
      );

      const taxRate = 0.15;

      const taxprice = Number((subTotalPrice * taxRate).toFixed(2));
      const totalPrice = Number((subTotalPrice + taxprice).toFixed(2));

      const payload = {
        user: user?._id || null,
        operator: [operator[0]?._id],
        selectedServices, // ✅ sends all service details

        selectedProfessional: {
          _id: selectedProfessionalId._id,
          name: selectedProfessionalId.name,
          photo: selectedProfessionalId.photo,
        },
        selectedDateTime,
        totalTimeService: String(totalDuration),
        booking: {
          status: "booked",
          bookedDate: new Date(),
        },
        bookingtype: "user",
        payments: {
          paymentMethod: selectedPayment || "cash",
          status: "pending",
          paymentDate: null,
        },
        customer_info: {
          name,
          email,
          mobile,
          message,
        },
        subTotalPrice,
        taxprice,
        totalPrice,
        reminder: "no",
      };
      //---send socket

      console.log({ payload });

      const { response, err } = await bookingApi.createBooking(payload);

      if (response && response.data) {
        toast.success("🎉 Booking submitted!");
        dispatch(resetBookingState());
        navigate(`/bookings/${response.data.book._id}`);

        // ✅ Send socket notification with booking info
        socketId.emit("sendOrderNotification", {
          senderId: user._id,
          receiverId: operator[0]?._id,
          orderId: response.data.book._id,
          orderInfo: {
            services: selectedServices,
            time: selectedDateTime,
            staff: selectedProfessionalId.name,
            price: totalPrice,
            customer_info: {
              name,
              email,
              mobile,
              message,
            },
            payments: {
              paymentMethod: selectedPayment || "cash",
              status: "pending",
              paymentDate: null,
            },
            subTotalPrice,
            taxprice,
            totalPrice,
          },
        });
      } else {
        toast.error("❌ Failed to submit booking. Please try again.");
        console.error(err || "Unknown error");
      }
    } catch (error) {
      console.error("Booking Error:", error);
      toast.error("❌ An error occurred while booking.");
    } finally {
      setIsProcessing(false);
    }
  };

  //----- socket section -----------------
  const [onlineUsers, setOnlineUsers] = useState([]);

  useEffect(() => {
    if (user?._id) {
      socketId.emit("addUser", user._id);
      socketId.on("getUsers", (data) => setOnlineUsers(data));
    } else {
      dispatch(setAuthModalOpen(true));
    }
  }, [user]);

  const isOperatorOnline =
    operator && operator[0]?._id
      ? onlineUsers.some((u) => u.userId === operator[0]._id)
      : false;

  const isOnline = isOperatorOnline;

  if (isPageLoading) return <Loading />;

  if (notFound || !operator.length) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6">
        <h1 className="text-2xl font-bold text-red-500">Operator Not Found</h1>
        <p className="text-gray-500 mt-2">
          Please check your booking link or go back.
        </p>
        <button
          onClick={() => navigate("/")}
          className="mt-6 px-5 py-2 rounded-full bg-red-500 text-white hover:bg-red-600 transition-all"
        >
          ⬅ Back to Home
        </button>
      </div>
    );
  }

  <TodayOpeningSlot operator={operator[0]} />;

  return (
    <div className="container space-y-8 lg:space-y-10 mb-20">
      <Helmet>
        <title>{`Booking process | ${operator[0]?.name || ""}`}</title>
        <meta name="description" content={operator[0]?.town || ""} />
        <meta property="og:title" content={operator[0]?.title || ""} />
        <meta property="og:description" content={operator[0]?.email || ""} />
        <meta property="og:image" content={operator[0]?.photo || ""} />
      </Helmet>
      {/* Header section */}
      <div className="flex flex-col items-start text-left px-6 pt-12">
        <span className="text-sm tracking-wider text-text uppercase">
          Stay in touch with us
        </span>
        <h1 className="text-3xl sm:text-4xl font-bold text-redlight flex items-center gap-2">
          Booking Now
          {isOnline ? (
            <span className="ml-3 bg-green-500/20 text-green-400 text-xs sm:text-sm font-semibold px-2 py-1 rounded-full flex items-center gap-1">
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
              <span className="animate-pulse">Online</span>
            </span>
          ) : (
            <span className="ml-3 bg-red-500/20 text-red-400 text-xs sm:text-sm font-semibold px-2 py-1 rounded-full flex items-center gap-1">
              <span className="w-2 h-2 bg-red-400 rounded-full"></span>
              Offline
            </span>
          )}
        </h1>
        <div className="w-[60px] h-[2px] bg-redlight mt-1" />
        <p className="italic text-textGray text-sm mt-2">
          Please field the form and submit (*)
        </p>
      </div>
      {/* Main Form */}
      <div className="w-full px-0 Py-10">
        <div className="flex flex-col lg:flex-row gap-10">
          <div className="flex-1 space-y-8">
            <div className="flex-1 border-0 lg:border border-border rounded-xl p-0 lg:p-6 bg-neutral-900 text-white min-h-[50vh]">
              <div className="border-b border-border pb-4 mb-6">
                <span className="text-4xl font-bold text-redlight">
                  {currentStep + 1}
                </span>
                <span className="text-lg text-neutral-400 ml-1">/ 6</span>
              </div>
              <AnimatePresence mode="wait">
                {currentStep === 0 && (
                  <motion.div
                    key="step1"
                    initial={{ opacity: 0, x: -50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 50 }}
                    transition={{ duration: 0.3 }}
                  >
                    {/* <BookingFormStepOne /> */}

                    <MultiSelectModal
                      label={"Services"}
                      totalTime={totalTimeService}
                      setTotalTime={setTotalTimeService}
                      options={services}
                      serviceCategory={serviceCategory}
                      selectedId={selectedServiceId}
                      setSelectedId={setSelectedServiceId}
                      selectedValues={selectedServices}
                      setSelectedValues={setSelectedServices}
                    />
                  </motion.div>
                )}
                {currentStep === 1 && (
                  <motion.div
                    key="step2"
                    initial={{ opacity: 0, x: -50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 50 }}
                    transition={{ duration: 0.3 }}
                  >
                    {" "}
                    <SelectProfessional
                      label={"Staffs"}
                      staffs={staffs}
                      selectedId={selectedProfessionalId}
                      selectedServices={selectedServiceId}
                      onChange={setSelectedProfessionalId}
                      // onChange={(staff) => {
                      //   setSelectedProfessionalId(staff);
                      //   setTimeout(() => {
                      //     setCurrentStep(2); // Automatically go to TimeSelector step
                      //   }, 300); // Small delay for modal closing animation
                      // }}
                    />
                  </motion.div>
                )}
                {currentStep === 2 && (
                  <motion.div
                    key="step3"
                    initial={{ opacity: 0, x: -50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 50 }}
                    transition={{ duration: 0.3 }}
                  >
                    <TimeSlotSelector
                      selectedDateTime={selectedDateTime}
                      setSelectedDateTime={setSelectedDateTime}
                      staffs={staffs}
                      selectedProfessionalId={selectedProfessionalId}
                      totalDurationMins={totalTimeService}
                    />
                  </motion.div>
                )}
                {currentStep === 3 && (
                  <motion.div
                    key="step4"
                    initial={{ opacity: 0, x: -50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 50 }}
                    transition={{ duration: 0.3 }}
                  >
                    <PaymentMethodSelector
                      selected={selectedPayment}
                      onChange={(value) => {
                        setSelectedPayment(value);

                        // Optional: auto advance to next step
                        // setTimeout(() => setCurrentStep((prev) => prev + 1), 300);
                      }}
                    />
                  </motion.div>
                )}
                {currentStep === 4 && (
                  <motion.div
                    key="step5"
                    initial={{ opacity: 0, x: -50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 50 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ContactInfo
                      name={name}
                      setName={setName}
                      email={email}
                      setEmail={setEmail}
                      mobile={mobile}
                      setMobile={setMobile}
                      message={message}
                      setMessage={setMessage}
                    />
                  </motion.div>
                )}

                {currentStep === 5 && (
                  <motion.div
                    key="step6"
                    initial={{ opacity: 0, x: -50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 50 }}
                    transition={{ duration: 0.3 }}
                  >
                    <SummaryCard
                      name={name}
                      email={email}
                      mobile={mobile}
                      selectedServices={selectedServices}
                      selectedProfessionalId={selectedProfessionalId}
                      staffs={staffs}
                      selectedDateTime={selectedDateTime}
                      message={message}
                      paymentMethod={bookingPaymentMethod} // ✅ Add this line
                      store={operator[0]}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            {!isStepValid() && (
              <p className="text-sm text-red-500 mt-1">
                Please complete this step to continue.
              </p>
            )}
            {!isValid && currentStep === 3 && (
              <p className="text-sm text-yellowlight mt-1">{stepError}</p>
            )}

            <div className="flex lg:justify-end justify-center space-x-6">
              {!isFirstStep && (
                <Button
                  onClick={handleBack}
                  type="button"
                  className="h-10 w-40 font-medium border border-yellowlight text-text bg-hidden border-neutral-200 text-neutral-700 dark:bg-neutral-900 dark:text-neutral-300 dark:border-neutral-700 hover:bg-yellowdark dark:hover:bg-neutral-800"
                >
                  Go back
                </Button>
              )}

              {!isLastStep ? (
                <Button
                  onClick={handleNext}
                  type="button"
                  disabled={!isStepValid()}
                  className={`relative h-10 w-40 font-medium ${
                    isStepValid()
                      ? "bg-redlight text-reddark hover:bg-reddark hover:text-text hover:border-reddark border border-redlight"
                      : "bg-gray-800 text-gray-600 cursor-not-allowed border border-border"
                  }`}
                >
                  {isProcessing ? (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <ProcessingBar />
                    </div>
                  ) : (
                    "Continue"
                  )}
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={async () => {
                    if (!user) {
                      dispatch(setAuthModalOpen(true));
                    } else {
                      await handleSubmit();
                    }
                  }}
                  disabled={isProcessing}
                  className={`relative h-10 w-40 font-medium ${
                    isProcessing
                      ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                      : "bg-redlight text-reddark hover:bg-reddark hover:text-text"
                  }`}
                >
                  {isProcessing ? (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <ProcessingBar />
                    </div>
                  ) : user ? (
                    "Booking"
                  ) : (
                    "Log in"
                  )}
                </Button>
              )}
            </div>
          </div>

          <OperatorSidebar operator={operator[0]} />
        </div>
      </div>
    </div>
  );
};

export default BookingProcess;
